"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../supabaseClient";
import Header from "../../../components/IntranetHeader";
import Footer from "../../../components/Footer";
import { formatProfileName } from "../../../utils/profileName";

function formatDateTime(value) {
  if (!value) return "To be confirmed";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "To be confirmed";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function calculateAgeFromDob(value, onDateValue) {
  if (!value) return null;
  const dob = new Date(`${value}T00:00:00`);
  const onDate = onDateValue ? new Date(onDateValue) : new Date();
  if (Number.isNaN(dob.getTime()) || Number.isNaN(onDate.getTime())) return null;

  let age = onDate.getFullYear() - dob.getFullYear();
  const monthDifference = onDate.getMonth() - dob.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && onDate.getDate() < dob.getDate())
  ) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

function requestTypeLabel(value) {
  if (value === "reschedule") return "Reschedule request";
  if (value === "cancellation") return "Cancellation request";
  return "New appointment request";
}

export default function IntranetAppointments({ session, profile }) {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPast, setShowPast] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requestActionId, setRequestActionId] = useState(null);
  const [requestClinicians, setRequestClinicians] = useState({});
  const [requestError, setRequestError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setRequestError("");

    const [appointmentResult, requestResult, clinicianResult] = await Promise.all([
      supabase
        .from("appointments")
        .select(
          `
          id,
          patient_full_name,
          patient_age,
          roleplay_name,
          department,
          clinician (id, full_name, name_prefix),
          appointment_datetime,
          duration_minutes,
          location,
          appointment_status,
          status
        `,
        )
        .order("appointment_datetime", { ascending: true }),
      supabase
        .from("appointment_requests")
        .select(
          `
          id,
          portal_account_id,
          appointment_id,
          request_type,
          requested_department,
          requested_location,
          requested_datetime,
          requested_duration_minutes,
          patient_reason,
          status,
          assigned_clinician_id,
          created_at,
          reviewed_at,
          resulting_appointment_id,
          patient:patient_portal_accounts!appointment_requests_portal_account_id_fkey (
            id,
            roleplay_name,
            roleplay_nhs_number,
            roblox_username,
            roblox_display_name,
            date_of_birth,
            discord_id,
            discord_username
          ),
          current_appointment:appointments!appointment_requests_appointment_id_fkey (
            id,
            patient_full_name,
            roleplay_name,
            department,
            clinician,
            appointment_datetime,
            duration_minutes,
            location,
            discord_id,
            discord_name,
            appointment_status,
            status
          )
        `,
        )
        .eq("status", "Pending")
        .order("created_at", { ascending: true }),
      supabase
        .from("profiles")
        .select("id, full_name, name_prefix, position, role, suspended")
        .order("full_name", { ascending: true }),
    ]);

    if (appointmentResult.error) {
      console.error("Unable to load appointments:", appointmentResult.error);
    } else {
      setAppointments(appointmentResult.data || []);
    }

    if (requestResult.error) {
      console.error("Unable to load appointment requests:", requestResult.error);
      setRequestError("Appointment requests could not be loaded.");
      setRequests([]);
    } else {
      const nextRequests = requestResult.data || [];
      setRequests(nextRequests);
      setRequestClinicians((current) => {
        const next = { ...current };
        nextRequests.forEach((request) => {
          if (!next[request.id]) {
            next[request.id] =
              request.assigned_clinician_id || request.current_appointment?.clinician || "";
          }
        });
        return next;
      });
    }

    if (clinicianResult.error) {
      console.error("Unable to load clinicians:", clinicianResult.error);
      setClinicians([]);
    } else {
      const staffOnly = (clinicianResult.data || []).filter((person) => {
        const position = String(person.position || "").trim().toLowerCase();
        const role = String(person.role || "").trim().toLowerCase();
        return position !== "patient" && role !== "patient" && person.suspended !== true;
      });
      setClinicians(staffOnly);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredAppointments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return appointments.filter(
      (appointment) =>
        !query ||
        String(appointment.patient_full_name || "").toLowerCase().includes(query) ||
        String(appointment.roleplay_name || "").toLowerCase().includes(query) ||
        formatProfileName(appointment.clinician, "").toLowerCase().includes(query),
    );
  }, [appointments, searchQuery]);

  const isCancelledAppointment = (appointment) =>
    appointment.status === false ||
    String(appointment.appointment_status || "").trim().toLowerCase() === "cancelled";

  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const startOfTomorrow = new Date(startOfToday);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

  const todayAppointments = filteredAppointments.filter((appointment) => {
    if (isCancelledAppointment(appointment)) return false;
    const date = new Date(appointment.appointment_datetime);
    return date >= startOfToday && date < startOfTomorrow;
  });

  const upcomingAppointments = filteredAppointments.filter((appointment) => {
    if (isCancelledAppointment(appointment)) return false;
    return new Date(appointment.appointment_datetime) >= startOfTomorrow;
  });

  const pastAppointments = filteredAppointments.filter((appointment) => {
    if (isCancelledAppointment(appointment)) return false;
    return new Date(appointment.appointment_datetime) < startOfToday;
  });

  const cancelledAppointments = filteredAppointments.filter(isCancelledAppointment);

  async function sendAppointmentDm(appointmentId, discordId, clinicianId) {
    if (!appointmentId || !discordId || !clinicianId) return;
    try {
      const response = await fetch("/api/send-appointment-dm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          appointmentId,
          patientDiscordId: discordId,
          clinicianProfileId: clinicianId,
        }),
      });
      if (!response.ok) {
        console.error("Appointment DM failed:", await response.text());
      }
    } catch (error) {
      console.error("Appointment DM failed:", error);
    }
  }

  async function approveRequest(request) {
    if (requestActionId) return;
    setRequestError("");
    setRequestActionId(request.id);

    try {
      const portal = request.patient;
      const currentAppointment = request.current_appointment;
      const selectedClinician = requestClinicians[request.id] || "";

      if (request.request_type !== "cancellation" && !selectedClinician) {
        throw new Error("Select a clinician before approving this request.");
      }

      let resultingAppointmentId = request.appointment_id || null;
      let patientDiscordId = portal?.discord_id || currentAppointment?.discord_id || null;

      if (request.request_type === "new_booking") {
        if (!portal) throw new Error("The patient portal account could not be loaded.");
        if (!request.requested_datetime) throw new Error("The requested date and time is missing.");

        const patientName =
          portal.roleplay_name || portal.roblox_display_name || portal.roblox_username || "Patient";
        const age = calculateAgeFromDob(portal.date_of_birth, request.requested_datetime);

        const { data: createdAppointment, error: createError } = await supabase
          .from("appointments")
          .insert([
            {
              portal_account_id: portal.id,
              patient_id: null,
              patient_full_name: patientName,
              patient_age: age,
              patient_dob: portal.date_of_birth || null,
              roblox_username: portal.roblox_username || null,
              roleplay_name: portal.roleplay_name || patientName,
              discord_name: portal.discord_username || null,
              discord_id: portal.discord_id || null,
              department: request.requested_department || "Outpatients",
              clinician: selectedClinician,
              appointment_datetime: request.requested_datetime,
              duration_minutes: request.requested_duration_minutes || 30,
              location: request.requested_location || "Prince George University Hospital",
              preparation: null,
              notes: request.patient_reason || null,
              appointment_status: "Scheduled",
              status: true,
            },
          ])
          .select("id, discord_id")
          .single();

        if (createError) throw createError;
        resultingAppointmentId = createdAppointment.id;
        patientDiscordId = createdAppointment.discord_id || patientDiscordId;
      } else if (request.request_type === "reschedule") {
        if (!request.appointment_id) throw new Error("The linked appointment could not be found.");
        if (!request.requested_datetime) throw new Error("The requested date and time is missing.");

        const { error: updateError } = await supabase
          .from("appointments")
          .update({
            clinician: selectedClinician,
            appointment_datetime: request.requested_datetime,
            location:
              request.requested_location ||
              currentAppointment?.location ||
              "Prince George University Hospital",
            patient_age: calculateAgeFromDob(portal?.date_of_birth, request.requested_datetime),
            ...(portal?.date_of_birth ? { patient_dob: portal.date_of_birth } : {}),
            appointment_status: "Scheduled",
            status: true,
            ...(portal?.discord_id ? { discord_id: portal.discord_id } : {}),
            ...(portal?.discord_username ? { discord_name: portal.discord_username } : {}),
          })
          .eq("id", request.appointment_id);

        if (updateError) throw updateError;
        resultingAppointmentId = request.appointment_id;
      } else if (request.request_type === "cancellation") {
        if (!request.appointment_id) throw new Error("The linked appointment could not be found.");

        const { error: cancelError } = await supabase
          .from("appointments")
          .update({
            status: false,
            appointment_status: "Cancelled",
          })
          .eq("id", request.appointment_id);

        if (cancelError) throw cancelError;
        resultingAppointmentId = request.appointment_id;
      }

      const { error: requestUpdateError } = await supabase
        .from("appointment_requests")
        .update({
          status: "Approved",
          assigned_clinician_id:
            request.request_type === "cancellation"
              ? currentAppointment?.clinician || null
              : selectedClinician,
          reviewed_by: session?.user?.id || null,
          reviewed_at: new Date().toISOString(),
          resulting_appointment_id: resultingAppointmentId,
        })
        .eq("id", request.id);

      if (requestUpdateError) throw requestUpdateError;

      const clinicianForDm =
        request.request_type === "cancellation"
          ? currentAppointment?.clinician || selectedClinician
          : selectedClinician;
      await sendAppointmentDm(resultingAppointmentId, patientDiscordId, clinicianForDm);

      if (portal?.id) {
        const type = request.request_type;
        const subject =
          type === "cancellation"
            ? "Appointment cancelled"
            : type === "reschedule"
              ? "Appointment rescheduled"
              : "Appointment request approved";

        const targetDate = request.requested_datetime || currentAppointment?.appointment_datetime;
        const targetLocation =
          request.requested_location ||
          currentAppointment?.location ||
          "Prince George University Hospital";

        const body =
          type === "cancellation"
            ? "Your cancellation request has been approved and the appointment is now cancelled."
            : `Your appointment request has been approved for ${targetDate ? new Date(targetDate).toLocaleString("en-GB") : "the agreed time"} at ${targetLocation}.`;

        const { error: portalMessageError } = await supabase.from("patient_messages").insert({
          portal_account_id: portal.id,
          patient_id: null,
          subject,
          body,
          category: "Appointment",
          action_url: "/patient-portal",
          created_by: session?.user?.id || null,
        });

        if (portalMessageError) {
          console.error("Unable to send appointment portal message:", portalMessageError);
        }
      }

      await fetchData();
    } catch (error) {
      console.error("Unable to approve appointment request:", error);
      setRequestError(error?.message || "The appointment request could not be approved.");
    } finally {
      setRequestActionId(null);
    }
  }

  async function declineRequest(request) {
    if (requestActionId) return;
    setRequestActionId(request.id);
    setRequestError("");
    try {
      const { error } = await supabase
        .from("appointment_requests")
        .update({
          status: "Declined",
          reviewed_by: session?.user?.id || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", request.id);
      if (error) throw error;
      await fetchData();
    } catch (error) {
      console.error("Unable to decline appointment request:", error);
      setRequestError("The appointment request could not be declined.");
    } finally {
      setRequestActionId(null);
    }
  }

  function renderAppointmentsTable(items, emptyText, includeStatus = false) {
    const columnCount = includeStatus ? 7 : 6;

    return (
      <table className="nhsuk-table nhsuk-table--striped nhsuk-u-width-full mb-6">
        <thead>
          <tr>
            <th>When</th>
            <th>Patient</th>
            <th>Roleplay Name</th>
            <th>Clinician</th>
            <th>Location</th>
            {includeStatus && <th>Status</th>}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan={columnCount} className="text-center">
                {emptyText}
              </td>
            </tr>
          )}
          {items.map((appointment) => (
            <tr key={appointment.id}>
              <td>{formatDateTime(appointment.appointment_datetime)}</td>
              <td>{appointment.patient_full_name}</td>
              <td>{appointment.roleplay_name || "–"}</td>
              <td>{appointment.clinician ? formatProfileName(appointment.clinician) : "–"}</td>
              <td>{appointment.location || "Prince George University Hospital"}</td>
              {includeStatus && (
                <td>
                  {isCancelledAppointment(appointment)
                    ? "Cancelled"
                    : appointment.appointment_status || "Completed"}
                </td>
              )}
              <td>
                <button
                  className="nhsuk-button nhsuk-button--small"
                  type="button"
                  onClick={() => navigate(`/intranet/ecamis/appointment/${appointment.id}`)}
                >
                  Open
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header session={session} profile={profile} />

      <main className="nhsuk-width-container p-4">
        <div className="pguh-appointments-heading-row">
          <div>
            <h1 className="nhsuk-heading-l mb-1">Appointments</h1>
            <p className="nhsuk-body-m">Manage scheduled appointments and patient requests.</p>
          </div>
          <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={fetchData} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        <section className="pguh-ecamis-request-panel" aria-labelledby="appointment-requests-heading">
          <div className="pguh-ecamis-request-panel__header">
            <div>
              <span className="pguh-eyebrow">Patient portal</span>
              <h2 id="appointment-requests-heading" className="nhsuk-heading-m">Appointment requests</h2>
              <p>Review booking, rescheduling and cancellation requests submitted by patients.</p>
            </div>
            <strong className="pguh-ecamis-request-count">{requests.length} pending</strong>
          </div>

          {requestError && <div className="pguh-timesheet-message pguh-timesheet-message--error">{requestError}</div>}

          {requests.length === 0 ? (
            <div className="pguh-empty-state">
              <h3>No pending requests</h3>
              <p>New patient appointment requests will appear here.</p>
            </div>
          ) : (
            <div className="pguh-ecamis-request-list">
              {requests.map((request) => {
                const portal = request.patient;
                const existing = request.current_appointment;
                const needsClinician = request.request_type !== "cancellation";

                return (
                  <article key={request.id} className="pguh-ecamis-request-card">
                    <div className="pguh-ecamis-request-card__main">
                      <span className={`pguh-ecamis-request-type pguh-ecamis-request-type--${request.request_type}`}>
                        {requestTypeLabel(request.request_type)}
                      </span>
                      <h3>{portal?.roleplay_name || existing?.patient_full_name || "Patient"}</h3>
                      <dl className="pguh-ecamis-request-details">
                        <div><dt>Roblox</dt><dd>{portal?.roblox_username || "Not available"}</dd></div>
                        <div><dt>NHS number</dt><dd>{portal?.roleplay_nhs_number || "Not available"}</dd></div>
                        <div><dt>Department</dt><dd>{request.requested_department || existing?.department || "Outpatients"}</dd></div>
                        <div>
                          <dt>Hospital</dt>
                          <dd>
                            {request.requested_location ||
                              existing?.location ||
                              "Prince George University Hospital"}
                          </dd>
                        </div>
                        <div><dt>Requested for</dt><dd>{request.request_type === "cancellation" ? "Cancellation" : formatDateTime(request.requested_datetime)}</dd></div>
                        {existing?.appointment_datetime && request.request_type !== "new_booking" && (
                          <div><dt>Current booking</dt><dd>{formatDateTime(existing.appointment_datetime)}</dd></div>
                        )}
                        <div><dt>Submitted</dt><dd>{formatDateTime(request.created_at)}</dd></div>
                      </dl>
                      {request.patient_reason && (
                        <div className="pguh-ecamis-request-reason">
                          <strong>Patient message</strong>
                          <p>{request.patient_reason}</p>
                        </div>
                      )}
                    </div>

                    <div className="pguh-ecamis-request-card__actions">
                      {needsClinician && (
                        <div className="nhsuk-form-group">
                          <label className="nhsuk-label" htmlFor={`request-clinician-${request.id}`}>Assign clinician</label>
                          <select
                            id={`request-clinician-${request.id}`}
                            className="nhsuk-select"
                            value={requestClinicians[request.id] || ""}
                            onChange={(event) =>
                              setRequestClinicians((current) => ({ ...current, [request.id]: event.target.value }))
                            }
                            disabled={requestActionId === request.id}
                          >
                            <option value="">– Select clinician –</option>
                            {clinicians.map((clinician) => (
                              <option key={clinician.id} value={clinician.id}>{formatProfileName(clinician)}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      <button
                        className="nhsuk-button"
                        type="button"
                        onClick={() => approveRequest(request)}
                        disabled={requestActionId === request.id}
                      >
                        {requestActionId === request.id ? "Processing…" : request.request_type === "cancellation" ? "Approve cancellation" : "Approve request"}
                      </button>
                      <button
                        className="nhsuk-button nhsuk-button--secondary"
                        type="button"
                        onClick={() => declineRequest(request)}
                        disabled={requestActionId === request.id}
                      >
                        Decline
                      </button>
                      {existing?.id && (
                        <button className="pguh-text-button" type="button" onClick={() => navigate(`/intranet/ecamis/appointment/${existing.id}`)}>
                          Open current appointment
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="pguh-appointments-list-panel">
          <div className="pguh-appointments-list-panel__header">
            <h2 className="nhsuk-heading-m">Scheduled appointments</h2>
          </div>

          <div className="flex flex-wrap items-center gap-4 mb-4">
            <input
              type="text"
              className="nhsuk-input flex-1 min-w-[200px]"
              placeholder="Search by patient, roleplay name, or clinician"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            <div
              className="nhsuk-checkboxes"
              style={{ display: "flex", flexWrap: "wrap", gap: "18px" }}
            >
              <div className="nhsuk-checkboxes__item">
                <input
                  className="nhsuk-checkboxes__input"
                  id="showPast"
                  type="checkbox"
                  checked={showPast}
                  onChange={(event) => setShowPast(event.target.checked)}
                />
                <label className="nhsuk-label nhsuk-checkboxes__label" htmlFor="showPast">
                  Show past appointments
                </label>
              </div>

              <div className="nhsuk-checkboxes__item">
                <input
                  className="nhsuk-checkboxes__input"
                  id="showCancelled"
                  type="checkbox"
                  checked={showCancelled}
                  onChange={(event) => setShowCancelled(event.target.checked)}
                />
                <label
                  className="nhsuk-label nhsuk-checkboxes__label"
                  htmlFor="showCancelled"
                >
                  Show cancelled appointments ({cancelledAppointments.length})
                </label>
              </div>
            </div>
          </div>

          <h3 className="nhsuk-heading-s mt-4">Today</h3>
          {renderAppointmentsTable(todayAppointments, "No appointments today.")}

          <h3 className="nhsuk-heading-s mt-4">Next</h3>
          {renderAppointmentsTable(upcomingAppointments, "No upcoming appointments.")}

          {showPast && (
            <>
              <h3 className="nhsuk-heading-s mt-4">Past appointments</h3>
              {renderAppointmentsTable(
                pastAppointments,
                "No past appointments.",
                true,
              )}
            </>
          )}

          {showCancelled && (
            <section
              className="nhsuk-u-margin-top-6"
              aria-labelledby="cancelled-appointments-heading"
            >
              <div className="nhsuk-warning-callout">
                <h3
                  id="cancelled-appointments-heading"
                  className="nhsuk-heading-s"
                >
                  Cancelled appointments
                </h3>
                <p className="nhsuk-body-m">
                  These appointments are retained for the patient record but are
                  not counted as active or upcoming appointments.
                </p>
              </div>

              {renderAppointmentsTable(
                cancelledAppointments,
                "No cancelled appointments.",
                true,
              )}
            </section>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
